// Shrinks a spec value's font size only when a single word is too wide to
// fit its cell even alone on its own line — normal multi-word wrapping is
// left alone. CSS sets overflow-wrap: normal on .spec-fact__value, so
// scrollWidth can only exceed clientWidth here when something genuinely
// can't wrap any further, which is exactly the case this should catch.
// Never breaks a word mid-character; shrinks instead, down to a floor set
// per element via --spec-fact-value-min-size.
//
// This can't be timed to land before the shopper has seen the value at
// its natural size (see the comment below on why it doesn't wait for
// `load`), so a value that needs shrinking eases into its final size
// instead of snapping to it — a correction that has to happen is at
// least allowed to look like it was supposed to. Applies only to a value
// that's actually being resized, and only around the one moment its
// settled result is revealed: never during the loop below, which needs
// the untransitioned, immediately-reflected size on every 0.5px step to
// read scrollWidth correctly — animating that would make each step
// measure a size it hasn't actually finished moving to yet.
function fitSpecValues() {
  document.querySelectorAll('.spec-fact__value').forEach((el) => {
    // The immediate pass and the document.fonts.ready pass below can
    // land close enough together (fonts.ready resolves almost instantly
    // whenever nothing is still loading, which is the common case on a
    // repeat visit with cached fonts) that the second call would otherwise
    // land mid-transition from the first. Resetting fontSize here would
    // interrupt that transition, and re-measuring against a size that
    // hasn't finished moving yet reads a mid-flight value, not a settled
    // one. The reveal already in flight is correct -- this element's
    // target isn't going to change between two calls a couple of
    // milliseconds apart -- so leave it alone and let it finish.
    if (el.classList.contains('spec-fact__value--fitting')) return;

    const previouslyFittedTo = el.dataset.fitSize;
    el.style.fontSize = '';

    const computed = getComputedStyle(el);
    const maxSize = parseFloat(computed.fontSize);
    const minSize = parseFloat(computed.getPropertyValue('--spec-fact-value-min-size')) || 11;
    // Largest size in 0.5px steps down from maxSize (at most 40 steps, never
    // more than one step under minSize) that fits. Binary search over the
    // steps: same answer as walking down one at a time, ~6 layouts not 40.
    const overflows = () => el.scrollWidth > el.clientWidth + 1;
    let size = maxSize;
    if (overflows()) {
      const maxSteps = Math.min(40, Math.max(0, Math.ceil((maxSize - minSize) / 0.5)));
      let lo = 1;
      let hi = maxSteps;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        el.style.fontSize = `${maxSize - mid * 0.5}px`;
        if (overflows()) lo = mid + 1;
        else hi = mid;
      }
      size = maxSize - hi * 0.5;
    }

    if (size === maxSize) {
      el.style.fontSize = '';
      delete el.dataset.fitSize;
      return;
    }

    if (previouslyFittedTo === String(size)) {
      // Same outcome this element already settled on -- a resize that
      // didn't actually change anything for it. Apply directly; nothing
      // changed here worth announcing with motion.
      el.style.fontSize = `${size}px`;
      return;
    }

    el.dataset.fitSize = String(size);
    el.style.fontSize = `${maxSize}px`;
    el.classList.add('spec-fact__value--fitting');
    // Two rAFs, not one: the first lets the browser commit "still at
    // natural size, transition now armed" as an actual rendered frame:
    // a transition only animates a change from a style that has already
    // been painted, and setting both the start and end state inside the
    // same task would let the browser coalesce them and skip straight to
    // the end value with no animation at all.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.fontSize = `${size}px`;
      });
    });
  });
}

// Was window.addEventListener('load', ...) -- the load event waits for
// every image and resource on the page to finish, not just this content,
// so on a product page with a full gallery the shrink could land a full
// second or more after the oversized text was already visible and
// painted. That gap is what Core Web Vitals scores as layout shift: text
// rendering at one size, then changing size (and therefore row height)
// once the user has already seen it.
//
// A module script is deferred to run right after the document is parsed
// (the same timing as a `defer` script), which is as early as this can
// possibly run — the elements it measures don't exist any sooner, since
// they're in the initial server-rendered HTML, not added later by JS. So
// the call below happens immediately, not attached to any event, rather
// than waiting for a signal that arrives long after the elements it needs
// are already there.
//
// The one thing worth waiting for is web fonts: the immediate pass runs
// against whatever font is active at that instant (typically the real
// font already, since fonts finish loading well before images do) --
// document.fonts.ready resolves as soon as every font actually used on
// the page has finished loading (or instantly if none are still
// pending), which is the earliest point a measurement is guaranteed
// accurate. Re-running after it resolves catches the rare case where a
// value's fit was measured against a fallback font whose metrics
// differed enough to change the outcome; for the common case where fonts
// are already loaded or cached, this second pass is a same-frame no-op.
fitSpecValues();

if (document.fonts && document.fonts.ready) {
  document.fonts.ready.then(fitSpecValues).catch(() => {});
}

// The fit depends only on width: skip height-only resizes (mobile URL bar)
// and run at most once per frame.
let fitWidth = window.innerWidth;
let fitFrame = 0;
window.addEventListener(
  'resize',
  () => {
    if (window.innerWidth === fitWidth || fitFrame) return;
    fitFrame = requestAnimationFrame(() => {
      fitFrame = 0;
      fitWidth = window.innerWidth;
      fitSpecValues();
    });
  },
  { passive: true }
);
